import React, { useState, useEffect } from "react";
import { Modal, Form }      from "react-bootstrap";
import Select               from "react-select";
import { toast }            from "react-toastify";
import CloseBtn             from "assets/icons/Close-1.svg";
import PayoutMethodsService from "services/payout-methods.service";
import WalletService        from "services/wallet.service";
import AuthService          from "services/auth.service";
import "./Withdraw.scss";

const AddAccountModal = ({ show, onClose, onCreated, onGateError }) => {
  const [bankOptions, setBankOptions]   = useState([]);
  const [holderName, setHolderName]     = useState("");
  const [accountNumber, setAccountNumber] = useState("");
  const [bank, setBank]                 = useState(null);
  const [description, setDescription]   = useState("");
  const [isDefault, setIsDefault]       = useState(false);
  const [errors, setErrors]             = useState({});
  const [submitting, setSubmitting]     = useState(false);

  useEffect(() => {
    if (!show) return;

    WalletService.listBanks().then(
      (data) => {
        const banks = Array.isArray(data) ? data : [];
        setBankOptions(banks.map((name) => ({ value: name, label: name })));
      },
      (error) => handleError(error)
    );
  }, [show]);

  function handleError(error) {
    if (error.response && error.response.status === 401) {
      AuthService.logout();
      window.location.replace("/eb/login");
    }
  }

  const resetForm = () => {
    setHolderName("");
    setAccountNumber("");
    setBank(null);
    setDescription("");
    setIsDefault(false);
    setErrors({});
  };

  const handleClose = () => {
    resetForm();
    onClose();
  };

  const validate = () => {
    const next = {};
    if (!holderName.trim())              next.holderName = "Account holder name is required";
    if (!accountNumber.trim())           next.accountNumber = "Account number is required";
    else if (!/^\d+$/.test(accountNumber.trim())) next.accountNumber = "Account number must contain digits only";
    if (!bank)                           next.bank = "Bank is required";

    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const handleSave = () => {
    if (!validate()) return;

    setSubmitting(true);

    const parameters = {
      account_holder_name: holderName.trim(),
      account_number:      accountNumber.trim(),
      bank_name:           bank.value,
      description:         description.trim(),
      is_default:          isDefault,
    };

    PayoutMethodsService.create(parameters).then(
      (data) => {
        onCreated(data.payout_methods || []);
        resetForm();
      },
      (error) => {
        if (error.response && error.response.status === 401) {
          AuthService.logout();
          window.location.replace("/eb/login");
        } else if (onGateError && onGateError(error)) {
          handleClose();
        } else {
          toast.error(error.response?.data?.error || "Failed to save account");
        }
      }
    ).then(() => {
      setSubmitting(false);
    });
  };

  return (
    <Modal className="add-account-modal" size="lg" show={ show } onHide={ handleClose } backdrop="static" centered>
      <Modal.Header className="p-0">
        <button type="button" className="close close-button position-relative pb-0" onClick={ handleClose }>
          <img src={ CloseBtn } alt="modal-close" className="w-30" />
        </button>
      </Modal.Header>

      <Modal.Body>
        <h4 className="fweight-700">Add new account.</h4>
        <p className="modal-subtitle">Provide your account details to securely manage your transactions.</p>

        <Form.Group className="mb-4">
          <Form.Label className="form-label">Account Holder Name<span className="required">*</span></Form.Label>
          <Form.Control
            value={ holderName }
            onChange={ (e) => setHolderName(e.target.value) }
            isInvalid={ !!errors.holderName }
          />
          { errors.holderName && <div className="field-error">{ errors.holderName }</div> }
        </Form.Group>

        <Form.Group className="mb-4">
          <Form.Label className="form-label">Account Number<span className="required">*</span></Form.Label>
          <Form.Control
            value={ accountNumber }
            onChange={ (e) => setAccountNumber(e.target.value) }
            isInvalid={ !!errors.accountNumber }
          />
          { errors.accountNumber && <div className="field-error">{ errors.accountNumber }</div> }
        </Form.Group>

        <Form.Group className="mb-4">
          <Form.Label className="form-label">Bank<span className="required">*</span></Form.Label>
          <Select
            classNamePrefix="bank-select"
            className="bank-select"
            placeholder=""
            options={ bankOptions }
            value={ bank }
            onChange={ (option) => setBank(option) }
          />
          { errors.bank && <div className="field-error">{ errors.bank }</div> }
        </Form.Group>

        <Form.Group className="mb-4">
          <Form.Label className="form-label">Description</Form.Label>
          <Form.Control
            value={ description }
            onChange={ (e) => setDescription(e.target.value) }
          />
        </Form.Group>

        <Form.Group className="mb-3">
          <Form.Label className="form-label">Additional Setting</Form.Label>
          <div className="toggle-row">
            <span className="toggle-text">Set as default</span>
            <label className="toggle-switch">
              <input
                type="checkbox"
                checked={ isDefault }
                onChange={ (e) => setIsDefault(e.target.checked) }
              />
              <span className="toggle-slider" />
            </label>
          </div>
        </Form.Group>
      </Modal.Body>

      <Modal.Footer className="d-block">
        <div className="modal-footer-actions">
          <button type="button" className="btn btn-coral" onClick={ handleSave } disabled={ submitting }>
            Save Account
          </button>
          <button type="button" className="btn btn-cancel" onClick={ handleClose }>
            Cancel
          </button>
        </div>
      </Modal.Footer>
    </Modal>
  );
};

export default AddAccountModal;
